/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { FiArrowDown, FiMoreHorizontal } from "react-icons/fi";
import { useUI } from "../../theme/ThemeProvider";
import { useNavigate } from "react-router-dom";
import ResponsiveTable from "../../components/ResponsiveTable";
import clsx from "clsx";

export default function RecentOrders() {
  const { theme, lang, colors } = useUI();
  const isDark = theme === "dark";
  const isArabic = lang === "ar";
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const baseURL = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, '');
  const tenantId = parseInt(localStorage.getItem("tenantId") || "0");

  const ORDER_STATUSES = [
    { id: 1, ar: "جديد", en: "New", key: "new", color: "bg-blue-100 text-blue-700 border-blue-200" },
    { id: 2, ar: "جاري التجهيز", en: "Preparing", key: "preparing", color: "bg-yellow-100 text-yellow-700 border-yellow-200" },
    { id: 3, ar: "جاهز", en: "Ready", key: "ready", color: "bg-purple-100 text-purple-700 border-purple-200" },
    { id: 4, ar: "جاري التوصيل", en: "In Delivery", key: "delivering", color: "bg-indigo-100 text-indigo-700 border-indigo-200" },
    { id: 5, ar: "مكتمل", en: "Completed", key: "delivered", color: "bg-green-100 text-green-700 border-green-200" },
    { id: 6, ar: "مسترجع", en: "Returned", key: "returned", color: "bg-orange-100 text-orange-700 border-orange-200" },
    { id: 8, ar: "مسترجع جزئيًا", en: "Partially Returned", key: "partially_returned", color: "bg-teal-100 text-teal-700 border-teal-200" },
    { id: 7, ar: "ملغي", en: "Cancelled", key: "cancelled", color: "bg-red-100 text-red-700 border-red-200" },
    { id: 9, ar: "فشل التوصيل", en: "Delivery Failed", key: "failed", color: "bg-black text-white border-gray-800" },
  ];

  const normalizeStatusKey = (raw) => {
    if (!raw) return "new";
    const v = raw.toString().toLowerCase().trim();
    const found = ORDER_STATUSES.find(s =>
      s.ar === raw ||
      s.en.toLowerCase() === v ||
      s.key === v
    );
    return found ? found.key : "new";
  };

  useEffect(() => {
    const fetchRecentOrders = async () => {
      if (!tenantId || tenantId === -1) return;

      try {
        setLoading(true);
        const token = localStorage.getItem("authToken");

        const params = new URLSearchParams();
        params.append('tenantId', tenantId.toString());
        params.append('statusId', '-1');

        const res = await fetch(
          `${baseURL}/GetTenantOrders?${params.toString()}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "Accept": "application/json",
              "Authorization": token ? `Bearer ${token}` : "",
            },
          }
        );

        if (!res.ok) throw new Error("API call failed");
        const data = await res.json();

        if (Array.isArray(data)) {
          // De-duplicate ONLY by unique orderId (UUID)
          const unique = [];
          const seenIds = new Set();
          data.forEach(o => {
            if (o.orderId && !seenIds.has(o.orderId)) {
              seenIds.add(o.orderId);
              unique.push(o);
            }
          });

          const sortedOrders = unique
            .sort((a, b) => {
              const dateA = new Date(a.orderDate || a.createdAt).getTime();
              const dateB = new Date(b.orderDate || b.createdAt).getTime();
              return dateB - dateA;
            })
            .slice(0, 5); // Pick top 3 recent orders
          setOrders(sortedOrders);
        }
      } catch (err) {
        console.error("GetTenantOrders error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentOrders();
  }, [baseURL, tenantId]);

  const calcOrderAmount = (order) => {
    if (Array.isArray(order.details) && order.details.length > 0) {
      return order.details.reduce((sum, i) => sum + (Number(i.lineTotal) || 0), 0);
    }
    return Number(order.netAmount || order.totalAmount || 0);
  };

  const parseOrderDate = (value) => {
    if (!value) return "-";
    // Handle the specific format from API: "1/25/2026 5:12PM" -> "1/25/2026 5:12 PM"
    const fixed = value.toString().replace(/\s+/g, " ").replace(/(\d)(AM|PM)/i, "$1 $2");
    const d = Date.parse(fixed);
    if (!isNaN(d)) {
      return new Date(d).toISOString().split('T')[0];
    }
    return value;
  };

  const processedOrders = useMemo(() => {
    return orders.map((order) => {
      const statusKey = normalizeStatusKey(order.status || order.statusName);
      return {
        id: order.orderNo || order.orderId?.slice(-6) || "N/A",
        customer: order.customerName || (isArabic ? "غير محدد" : "Unknown"),
        phone: order.customerPhone1 || "",
        governorate: order.city || "",
        date: parseOrderDate(order.orderDate || order.createdAt),
        totalAmount: calcOrderAmount(order),
        currency: isArabic ? "د.ع" : "IQD",
        statusKey,
        isPaid: order.isPaid === true || (order.paymentStatus || "").toLowerCase() === "paid",
        isManual: order.isManual === 1 || order.isManual === true || order.transactionId === "manual",
        rawOrder: order,
      };
    });
  }, [orders, isArabic]);

  return (
    <div dir={isArabic ? "rtl" : "ltr"} className="w-full">
      <motion.section
        initial={{ opacity: 0, y: 25 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full"
      >
        <h2
          className={`text-xl font-bold mb-4 ${isDark ? "text-white" : "text-gray-900"}`}
        >
          {isArabic ? "أخر الطلبات" : "Recent Orders"}
        </h2>

        {loading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin h-10 w-10 border-b-2 border-purple-600 rounded-full" />
          </div>
        ) : processedOrders.length > 0 ? (
          <div className={clsx(
            "rounded-2xl border overflow-hidden shadow-lg transition-all duration-300",
            isDark ? "bg-[#1e293b] border-[#334155]" : "bg-white border-gray-100"
          )}>
            <div className={isDark ? "bg-[#1e293b]" : "bg-white"}>
              <ResponsiveTable
                columns={[
                  { header: isArabic ? "رقم الطلب" : "Order No" },
                  { header: isArabic ? "العميل" : "Customer" },
                  { header: isArabic ? "حالة الدفع" : "Payment Status" },
                  { header: isArabic ? "المحافظة" : "Governorate" },
                  { header: isArabic ? "المجموع" : "Total" },
                  { header: isArabic ? "الحالة" : "Status" },
                  { header: isArabic ? "تاريخ الإنشاء" : "Created At" },
                  { header: isArabic ? "الإجراءات" : "Actions" },
                ]}
                data={processedOrders}
                searchable={false}
                sortable={false}
                renderRow={(order) => (
                  <>
                    <td className="py-4 px-4 whitespace-nowrap align-top">
                      <div className="flex flex-col">
                        <span className="font-bold text-[#5B2C6F] text-base">
                          {order.id}
                        </span>
                        <span className="text-xs text-gray-400 mt-1">
                          {order.isManual
                            ? (isArabic ? "طلب يدوي" : "Manual Order")
                            : (isArabic ? "المتجر الإلكتروني" : "Online Store")
                          }
                        </span>
                      </div>
                    </td>

                    <td className="py-4 px-4 whitespace-nowrap align-top">
                      <div className="flex flex-col">
                        <span className="font-semibold text-sm text-gray-900 dark:text-white">
                          {order.customer}
                        </span>
                        <span className="text-xs text-gray-500 mt-1" dir="ltr">
                          {order.phone || "-"}
                        </span>
                      </div>
                    </td>

                    <td className="py-4 px-4 whitespace-nowrap align-top">
                      <span className={clsx(
                        "inline-flex px-2 py-1 rounded text-xs font-medium",
                        order.isPaid
                          ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                          : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                      )}>
                        {order.isPaid ? (isArabic ? "مدفوع" : "Paid") : (isArabic ? "غير مدفوع" : "Unpaid")}
                      </span>
                    </td>

                    <td className="py-4 px-4 whitespace-nowrap align-top">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        {order.governorate || "-"}
                      </span>
                    </td>

                    <td className="py-4 px-4 whitespace-nowrap align-top">
                      <div className="flex flex-col">
                        <span className="font-bold text-gray-900 dark:text-white">
                          {order.totalAmount.toLocaleString()}
                        </span>
                        <span className="text-xs text-gray-400 uppercase">
                          {order.currency}
                        </span>
                      </div>
                    </td>

                    <td className="py-4 px-4 whitespace-nowrap align-top">
                      <span className={clsx(
                        "text-xs px-3 py-1.5 rounded-lg font-bold flex items-center justify-center min-w-[100px] border transition-colors",
                        order.statusKey === "new" ? "bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-200 dark:border-blue-800" :
                          order.statusKey === "preparing" ? "bg-orange-100 text-orange-800 border-orange-200 dark:bg-orange-900/40 dark:text-orange-200 dark:border-orange-800" :
                            order.statusKey === "ready" ? "bg-purple-100 text-purple-800 border-purple-200 dark:bg-purple-900/40 dark:text-purple-200 dark:border-purple-800" :
                              order.statusKey === "delivering" ? "bg-indigo-100 text-indigo-800 border-indigo-200 dark:bg-indigo-900/40 dark:text-indigo-200 dark:border-indigo-800" :
                                order.statusKey === "delivered" ? "bg-green-100 text-green-800 border-green-200 dark:bg-green-900/40 dark:text-green-200 dark:border-green-800" :
                                  order.statusKey === "cancelled" ? "bg-red-100 text-red-800 border-red-200 dark:bg-red-900/40 dark:text-red-200 dark:border-red-800" :
                                    "bg-gray-100 text-gray-800 border-gray-200"
                      )}>
                        {(() => {
                          const sObj = ORDER_STATUSES.find(t => t.key === order.statusKey);
                          return isArabic ? (sObj?.ar || order.statusKey) : (sObj?.en || order.statusKey);
                        })()}
                      </span>
                    </td>

                    <td className="py-4 px-4 whitespace-nowrap align-top">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        {order.date}
                      </span>
                    </td>

                    <td className="py-4 px-4 whitespace-nowrap text-center align-top">
                      <button
                        onClick={() => navigate(`/admin-store/orders-details/${order.rawOrder.orderId}`)}
                        className="p-2 rounded-lg text-gray-500  transition-colors border border-gray-200 dark:border-gray-700"
                      >
                        <FiMoreHorizontal size={20} />
                      </button>
                    </td>
                  </>
                )}
              />
            </div>
          </div>
        ) : (
          <div className={clsx(
            "text-center py-12 rounded-xl",
            isDark ? "bg-[#1e293b]" : "bg-white"
          )}>
            <p className={clsx(
              "text-base sm:text-lg transition-colors",
              isDark ? "text-gray-400" : "text-gray-600"
            )}>
              {isArabic ? "لا توجد طلبات حديثة" : "No recent orders found"}
            </p>
          </div>
        )}
      </motion.section>
    </div>
  );
}

